import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonComponent, ListTableComponent, ListTableDataLoader, PopupDialogComponent } from '@ppl/ui';
import { UserService } from '../../../../services/user.service';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import { NotificationService } from '../../../../shared/services/notification.service';
import { TimesheetPayrollProcessingService } from './timesheet-payroll-processing.service';

type RunDialogType = 'approval' | 'rejection' | 'warning';

/** Batch status the approve / reject endpoints accept a transition from. */
const LINE_BATCHED = 'Line Batched';

@Component({
  selector: 'ppl-timesheet-payroll-processing',
  standalone: true,
  imports: [FormsModule, ButtonComponent, ListTableComponent, PopupDialogComponent],
  templateUrl: './timesheet-payroll-processing.component.html',
  styleUrl: './timesheet-payroll-processing.component.css',
})
export class TimesheetPayrollProcessingComponent implements OnInit {
  @ViewChild(ListTableComponent) listTable!: ListTableComponent;

  private allowListStore = inject(AllowListAuthStore);

  pageId = PAGE_CONSTANTS.STAFF_TIMESHEET_PAYROLL_PROCESSING;
  isReadOnly = false;
  programName = '';
  lazyLoad = true;
  sharedDataKey = 'list_timesheet_payroll_runs';

  // Approve / reject dialog.
  showStatusDialog = false;
  dialogType: RunDialogType = 'approval';
  warningMessage = '';
  comments = '';
  selectedRows: any[] = [];
  isSubmitting = false;

  payrollRunsDataLoader: ListTableDataLoader = (payload) => this.payrollService.getPayrollRuns(payload);

  constructor(
    private userService: UserService,
    private router: Router,
    private notificationService: NotificationService,
    private payrollService: TimesheetPayrollProcessingService
  ) {}

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.setPageAccess();
  }

  /** Batch creation lives on its own screen so criteria can be built there. */
  onNewBatch(): void {
    this.router.navigate(['/home/addTimesheetPayrollBatch']);
  }

  // ─── Batch details ────────────────────────────────────────────────────────
  /**
   * Opens an existing batch on the batch screen. Details are fetched here and
   * handed over in navigation state so the screen renders already populated.
   */
  onCellClicked(event: any): void {
    const field = event?.col?.field;
    const rowData = event?.rowData;
    if (field !== 'batchInstanceId' || !rowData?.batchInstanceId) {
      return;
    }

    const batchInstanceId = rowData.batchInstanceId;
    this.payrollService.getBatchDetails(this.programName, batchInstanceId).subscribe({
      next: (details: any) => {
        this.router.navigate(['/home/addTimesheetPayrollBatch'], {
          state: { editData: details, batchInstanceId, batchRow: rowData },
        });
      },
      error: () => {
        this.notificationService.error('Error', 'Failed to load batch details');
      },
    });
  }

  // ─── Row drilldowns ───────────────────────────────────────────────────────
  onViewResults(batchInstanceId: string): void {
    this.router.navigate(['/home/timesheetPayrollRunResults'], { state: { batchInstanceId } });
  }

  onViewErrors(batchInstanceId: string): void {
    this.router.navigate(['/home/timesheetPayrollRunErrors'], { state: { batchInstanceId } });
  }

  // ─── Approve / reject ─────────────────────────────────────────────────────
  onListAction(event: any): void {
    const action = event?.button?.buttonAction;
    const selected = event?.selectedCheckBox ?? [];

    if (action !== 'approvePayrollRun' && action !== 'rejectPayrollRun') {
      return;
    }

    if (selected.some((row: any) => row?.batchStatus !== LINE_BATCHED)) {
      this.dialogType = 'warning';
      this.warningMessage =
        'Only Line Batched payroll runs can be approved or rejected. Please adjust your selection and try again.';
      this.showStatusDialog = true;
      return;
    }

    this.selectedRows = selected;
    this.comments = '';
    this.dialogType = action === 'approvePayrollRun' ? 'approval' : 'rejection';
    this.showStatusDialog = true;
  }

  onStatusConfirm(): void {
    if (this.dialogType === 'warning') {
      this.showStatusDialog = false;
      return;
    }

    if (this.isConfirmBlocked) {
      return;
    }

    const isApproval = this.dialogType === 'approval';
    const batchInstanceIds = this.selectedRows.map((row) => row.batchInstanceId);

    this.isSubmitting = true;
    this.payrollService
      .updateBatchStatus(this.programName, batchInstanceIds, isApproval, this.comments)
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.showStatusDialog = false;
          this.notificationService.success(
            'Success',
            `Payroll batch(es) ${isApproval ? 'approved' : 'rejected'} successfully`
          );
          this.reloadGrid();
        },
        error: () => {
          this.isSubmitting = false;
          this.showStatusDialog = false;
          this.notificationService.error(
            'Error',
            `Failed to ${isApproval ? 'approve' : 'reject'} the selected payroll batch(es)`
          );
        },
      });
  }

  /** Both approval and rejection post remarks, and rejection needs a reason. */
  get isConfirmBlocked(): boolean {
    if (this.isSubmitting) return true;
    return this.dialogType === 'rejection' && !this.comments.trim();
  }

  get statusDialogTitle(): string {
    if (this.dialogType === 'approval') return 'Approve Payroll Batch';
    if (this.dialogType === 'rejection') return 'Reject Payroll Batch';
    return 'Warning';
  }

  private reloadGrid(): void {
    this.listTable?.reloadDataFromSharedService(this.sharedDataKey);
  }

  private setPageAccess(): void {
    const authConfig = this.allowListStore.getConfig();
    if (authConfig && !authConfig.pages[this.pageId]) {
      //this.isReadOnly = true;
    }
  }
}
